<script lang="ts">
import { onMount } from 'svelte';
import type { ImageProps } from '$lib/types';
import {
    formatDirectBuilderImageSrcsetForAspectRatio,
    formatDirectBuilderImageUrlForAspectRatio,
} from '$lib/utils/formatImageUrl';
import { stripSiteDomain } from '$lib/utils/url.js';
import Link from '../Link/Link.svelte';
import {
    registerAutoplayCandidate,
    updateAutoplayCandidatePriority,
    updateAutoplayCandidateVisibility,
} from './card-grid-item-autoplay-registry';

const THUMBNAIL_IMAGE_SIZES =
    '(max-width: 767px) calc(100vw - 32px), (max-width: 1199px) calc(100vw - 48px), calc((100vw - 148px) / 2)';
const THUMBNAIL_IMAGE_PARAMS = 'format=webp&quality=72';
const THUMBNAIL_IMAGE_WIDTHS = [480, 640, 800, 960, 1280] as const;
const THUMBNAIL_IMAGE_FALLBACK_WIDTH = 960;
const THUMBNAIL_VIDEO_POSTER_WIDTH = 960;
const THUMBNAIL_VIDEO_POSTER_PARAMS = 'format=webp&quality=72';
const THUMBNAIL_ASPECT_RATIO = 16 / 9;

// Dynamic import for VideoPlayer to reduce initial bundle size
let VideoPlayerComponent:
    | typeof import('../../organisms/VideoPlayer/VideoPlayer.svelte').default
    | null = $state(null);
let isLoaded = $state(false);
let cardElement: HTMLDivElement | null = $state(null);
let supportsHoverPlayback = $state(false);
let prefersReducedMotion = $state(false);
let isHovering = $state(false);
let hasActivatedVideo = $state(false);
let isAutoplayVisible = $state(false);
let hasAutoplayOwnership = $state(false);
let visibilityObserver: IntersectionObserver | null = null;
let hoverMediaQuery: MediaQueryList | null = null;
let reducedMotionMediaQuery: MediaQueryList | null = null;
let unregisterAutoplayCandidate: (() => void) | null = null;
let hasLoadAttempted = false;
let hasMounted = $state(false);
let registeredAutoplayGroup = $state('');
let registeredAutoplayOwnerId = $state('');

let {
    title = '',
    description = '',
    customDescription = undefined,
    to = '',
    image = $bindable<ImageProps>({}),
    videoUrl: _unusedVideoUrl = '',
    muxId = '',
    autoplayVideo = false,
    autoplayGroup = '',
    autoplayOwnerId = '',
    autoplayPriority = 0,
    enableVideoHoverZoom = false,
    hideBlock = false,
    ...rest
} = $props();

// Transform URL from Builder.io
const transformedTo = $derived(stripSiteDomain(to));
const formattedThumbnail = $derived(
    formatDirectBuilderImageUrlForAspectRatio(
        image?.src,
        THUMBNAIL_IMAGE_FALLBACK_WIDTH,
        THUMBNAIL_ASPECT_RATIO,
        THUMBNAIL_IMAGE_PARAMS,
    ),
);
const thumbnailSrcset = $derived(
    formatDirectBuilderImageSrcsetForAspectRatio(
        image?.src,
        THUMBNAIL_IMAGE_WIDTHS,
        THUMBNAIL_ASPECT_RATIO,
        THUMBNAIL_IMAGE_PARAMS,
    ),
);
const formattedPoster = $derived(
    formatDirectBuilderImageUrlForAspectRatio(
        image?.src,
        THUMBNAIL_VIDEO_POSTER_WIDTH,
        THUMBNAIL_ASPECT_RATIO,
        THUMBNAIL_VIDEO_POSTER_PARAMS,
    ),
);
const hasVideoSource = $derived(Boolean(muxId));
const usesCoordinatedAutoplay = $derived(
    Boolean(autoplayVideo && autoplayGroup && autoplayOwnerId),
);
const canAutoplayVideoThumbnail = $derived(
    hasVideoSource && Boolean(image?.src) && supportsHoverPlayback && !prefersReducedMotion,
);
const canHoverVideoThumbnail = $derived(canAutoplayVideoThumbnail && supportsHoverPlayback);
const canUseVideoThumbnail = $derived(
    autoplayVideo ? canAutoplayVideoThumbnail : canHoverVideoThumbnail,
);
const shouldPlayVideo = $derived(
    canUseVideoThumbnail &&
        (autoplayVideo ? hasAutoplayOwnership && isAutoplayVisible : isHovering),
);
const shouldRenderVideoPlayer = $derived(
    canUseVideoThumbnail && (autoplayVideo ? hasAutoplayOwnership : hasActivatedVideo),
);

const updateVideoPlaybackCapabilities = () => {
    supportsHoverPlayback = hoverMediaQuery?.matches ?? false;
    prefersReducedMotion = reducedMotionMediaQuery?.matches ?? false;
};

const loadVideoPlayer = async () => {
    if (!hasVideoSource || VideoPlayerComponent || hasLoadAttempted) {
        return;
    }

    hasLoadAttempted = true;

    try {
        const module = await import('../../organisms/VideoPlayer/VideoPlayer.svelte');
        if (module?.default) {
            VideoPlayerComponent = module.default;
            isLoaded = true;
        } else {
            console.error('VideoPlayer component module.default is undefined');
            VideoPlayerComponent = null;
            isLoaded = true;
        }
    } catch (error) {
        console.error('Failed to load VideoPlayer component:', error);
        VideoPlayerComponent = null;
        isLoaded = true;
    }
};

const disconnectVisibilityObserver = () => {
    visibilityObserver?.disconnect();
    visibilityObserver = null;
};

const setupVisibilityObserver = () => {
    disconnectVisibilityObserver();

    if (!autoplayVideo || !cardElement || !canUseVideoThumbnail) {
        return;
    }

    visibilityObserver = new IntersectionObserver(
        (entries) => {
            const [entry] = entries;
            const isVisible = entry?.isIntersecting ?? false;

            isAutoplayVisible = isVisible;
            if (usesCoordinatedAutoplay) {
                updateAutoplayCandidateVisibility(
                    autoplayGroup,
                    autoplayOwnerId,
                    canUseVideoThumbnail && isVisible,
                );
            } else if (isVisible && canUseVideoThumbnail) {
                hasAutoplayOwnership = true;
            }
        },
        { threshold: 0.25 },
    );

    visibilityObserver.observe(cardElement);
};

const handlePreviewStart = () => {
    if (!canUseVideoThumbnail || autoplayVideo) {
        return;
    }

    isHovering = true;
    hasActivatedVideo = true;
};

const handlePreviewStop = () => {
    isHovering = false;
};

onMount(() => {
    isLoaded = !hasVideoSource;

    hoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    updateVideoPlaybackCapabilities();
    hasMounted = true;

    const onMediaQueryChange = () => {
        updateVideoPlaybackCapabilities();
    };

    hoverMediaQuery.addEventListener('change', onMediaQueryChange);
    reducedMotionMediaQuery.addEventListener('change', onMediaQueryChange);

    return () => {
        disconnectVisibilityObserver();
        unregisterAutoplayCandidate?.();
        unregisterAutoplayCandidate = null;
        hoverMediaQuery?.removeEventListener('change', onMediaQueryChange);
        reducedMotionMediaQuery?.removeEventListener('change', onMediaQueryChange);
    };
});

$effect(() => {
    const shouldLoadAutoplayVideo = autoplayVideo && canUseVideoThumbnail && isAutoplayVisible;

    if (shouldRenderVideoPlayer || shouldLoadAutoplayVideo) {
        void loadVideoPlayer();
    }
});

$effect(() => {
    setupVisibilityObserver();
});

$effect(() => {
    if (!hasMounted) {
        return;
    }

    const shouldRegisterAutoplayCandidate = usesCoordinatedAutoplay && canUseVideoThumbnail;

    if (!shouldRegisterAutoplayCandidate) {
        unregisterAutoplayCandidate?.();
        unregisterAutoplayCandidate = null;
        registeredAutoplayGroup = '';
        registeredAutoplayOwnerId = '';
        hasAutoplayOwnership = false;
        return;
    }

    const hasRegistrationChanged =
        !unregisterAutoplayCandidate ||
        registeredAutoplayGroup !== autoplayGroup ||
        registeredAutoplayOwnerId !== autoplayOwnerId;

    if (hasRegistrationChanged) {
        unregisterAutoplayCandidate?.();
        unregisterAutoplayCandidate = registerAutoplayCandidate(
            autoplayGroup,
            autoplayOwnerId,
            autoplayPriority,
            (isActiveOwner) => {
                hasAutoplayOwnership = isActiveOwner;
            },
        );
        registeredAutoplayGroup = autoplayGroup;
        registeredAutoplayOwnerId = autoplayOwnerId;
        return;
    }

    updateAutoplayCandidatePriority(autoplayGroup, autoplayOwnerId, autoplayPriority);
});

$effect(() => {
    if (!autoplayVideo) {
        return;
    }

    if (usesCoordinatedAutoplay) {
        updateAutoplayCandidateVisibility(
            autoplayGroup,
            autoplayOwnerId,
            canUseVideoThumbnail && isAutoplayVisible,
        );
        return;
    }

    hasAutoplayOwnership = canUseVideoThumbnail && isAutoplayVisible;
});

$effect(() => {
    if (!canUseVideoThumbnail) {
        isHovering = false;
        isAutoplayVisible = false;
        hasAutoplayOwnership = false;

        if (!autoplayVideo) {
            hasActivatedVideo = false;
        }
    }
});

$effect(() => {
    if (image.alt) {
        image.altText = image.alt;
        image.alt = undefined;
    } else if (image.altText === undefined) {
        image.altText = `Image from ${title}`;
    }
});
</script>

{#if !hideBlock}
    <div
        bind:this={cardElement}
        {...rest.attributes}
        class="card-grid-item {rest.attributes?.class} {rest.class}"
    >
        <Link
            to={transformedTo}
            {...rest}
            class={`container-link ${canUseVideoThumbnail ? 'container-link--video-enabled' : 'container-link--image-only'} ${enableVideoHoverZoom ? 'container-link--video-zoom-enabled' : ''}`}
            onpointerenter={handlePreviewStart}
            onpointerleave={handlePreviewStop}
            onfocus={handlePreviewStart}
            onblur={handlePreviewStop}
        >
            {#snippet text()}
                {#if shouldRenderVideoPlayer && isLoaded && VideoPlayerComponent}
                    {@const VideoPlayer = VideoPlayerComponent}
                    <div class="media-wrapper media-wrapper--video">
                        <VideoPlayer
                            {muxId}
                            poster={formattedPoster}
                            autoPlay={false}
                            shouldPlay={shouldPlayVideo}
                            resetOnPause={true}
                        />
                    </div>
                {:else if image?.src}
                    <div class="media-wrapper">
                        <div class="image-wrapper">
                            <img
                                src={formattedThumbnail}
                                srcset={thumbnailSrcset}
                                sizes={THUMBNAIL_IMAGE_SIZES}
                                alt={image.altText || title || ''}
                                class="builder-image img"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                {/if}
                <div class="text-wrapper">
                    {#if title}
                        <h2 class="title">{title}</h2>
                    {/if}
                    {#if customDescription}
                        <div class="description">
                            {@render customDescription()}
                        </div>
                    {:else if description}
                        <p class="description">{description}</p>
                    {/if}
                </div>
            {/snippet}
        </Link>
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;

    .card-grid-item :global(.container-link) {
        display: block;
        text-decoration: none;
    }

    .card-grid-item :global(.container-link:hover) {
        text-decoration: none;

        :global(img) {
            transform: scale(1.1) !important;
        }
    }

    .card-grid-item :global(.container-link--video-zoom-enabled:hover) :global(mux-video) {
        transform: scale(1.1);
    }

    .media-wrapper {
        position: relative;
    }

    .media-wrapper--video {
        aspect-ratio: var(--layout-media-ar);
        overflow: hidden;
    }

    .media-wrapper--video :global(.container--video-player) {
        height: 100%;
        width: 100%;
    }

    .image-wrapper {
        overflow: hidden;
        position: relative;
    }

    .image-wrapper :global(img),
    .media-wrapper--video :global(mux-video) {
        aspect-ratio: var(--layout-media-ar);
        object-fit: cover;
        transition: transform var(--timing-duration-long) !important;
        width: 100%;
    }

    .text-wrapper {
        max-width: 42rem;
        margin-top: 0.8rem;

        @include mq($from: tablet) {
            margin-top: 1.6rem;
        }
    }

    .title {
        @include typography.body-emphasis;
        text-decoration: none;
        color: var(--colour-dreamless-sleep);
        text-transform: uppercase;
    }

    .description {
        @include typography.body-base;
        color: var(--colour-squant);
        text-decoration: none;
    }
</style>
